import React from 'react';
import ReactSelect from 'react-select';
import classnames from 'classnames';
import Text from '../../Text/Text';
import Icon from '../../Icon/Icon';
import Fade from '../../Transitions/Fade/Fade';

import './Select.scss';

export interface SelectProps {
    /**
     * Ref select
     */
    ref?: any;
    /**
     * Id attr select
     */
    id?: string;
    /**
     * Name attr select
     */
    name: string;
    /**
     * Options
     */
    options?: { label: string; value: string | number }[];
    /**
     * Label attr select
     */
    label?: string;
    /**
     * Placeholder
     */
    placeholder?: string;
    /**
     * Message to show when there are no options
     */
    noOptionsMessage?: any;
    /**
     * Error message
     */
    error?: string;
    /**
     * Multiple
     * @default false
     */
    multiple?: boolean;
    /**
     * Grouped
     * @default false
     */
    grouped?: boolean;
    /**
     * Show tags if select is multiple
     * @default true
     */
    taggable?: boolean;
    /**
     * Show message on has values selected
     */
    unTaggableMessage?: any;
    /**
     * Hide selected options is multiple select
     * @default false
     */
    hideSelectedOptions?: boolean;
    /**
     * Disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Select class name styles
     */
    inputClassName?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Value select
     */
    value?: string | number | string[] | number[];
    /**
     * Function called on change checkbox
     */
    onChange?(value: any): any;
    /**
     * Other props
     */
    [otherProps: string]: any;
}

/*
 * Obtains the value that ReactSelect needs (an object with "label-value" or array of objects)
 * The param "value" can be a string or array of strings
 */
const getValueForReactSelect = (value: any, options: any, multiple: boolean = false, grouped: boolean = false): any => {
    if (Array.isArray(options) && options.length > 0) {
        if (multiple) {
            if (!value || !Array.isArray(value)) return [];
            return (value || []).map(v => getValueForReactSelect(v, options)).filter(v => v !== undefined);
        }
        if (typeof value === 'string' || typeof value === 'number') {
            if (grouped) {
                const groupedOptions = options.reduce((acc, option) => [...acc, ...option.options], []);
                const option = groupedOptions.find((o: any) => o.value === value);
                return option ? { value, label: option.label } : null;
            }
            const option = options.find((o: any) => o.value === value);
            return option ? { value, label: option.label } : null;
        }
    }
    return null;
};

/**
 * Option component
 * @param props // DOCS: https://react-select.com/props#option
 * @returns
 */
const Option = (props: any) => {
    const { innerProps, label, isMulti, isSelected, isFocused, isDisabled, className } = props;
    const classNames = classnames([
        'select__option',
        {
            'select__option--selected': isSelected,
            'select__option--focused': isFocused,
            'select__option--disabled': isDisabled,
            'select__option--multiple': isMulti,
        },
        className,
    ]);

    return (
        <div className={classNames} {...innerProps}>
            {isMulti ? (
                <div className="select__option-checkbox">
                    {isSelected ? <Icon name="check" size={22} color="white" /> : null}
                </div>
            ) : null}
            <Text tag="span" color={isSelected && !isMulti ? 'white' : undefined}>
                {label}
            </Text>
        </div>
    );
};

const ValueContainer = (props: any) => {
    const { label, message, getValue, selectProps, className } = props;
    const { placeholder, onMenuOpen } = selectProps;
    const { length } = getValue() || [];
    const text = `${message || label || ''} (${length})`;
    const classNames = classnames([
        'select__value-container select__value-container--custom',
        { select__placeholder: !length },
        className,
    ]);

    return (
        <div className={classNames} onClick={onMenuOpen}>
            {length ? text : placeholder}
        </div>
    );
};

/**
 * This component use react-select package
 * Doc: https://react-select.com/home
 * Props: https://react-select.com/props
 */
const Select: React.FC<SelectProps> = React.forwardRef(
    (
        {
            id = 'select',
            name,
            options = [],
            label,
            placeholder = 'Select...',
            noOptionsMessage = 'No options',
            value,
            error = false,
            disabled = false,
            multiple = false,
            grouped = false,
            taggable = false,
            unTaggableMessage = '',
            hideSelectedOptions = false,
            className,
            inputClassName,
            style,
            onChange = () => {},
            ...otherProps
        },
        ref
    ) => {
        const classNames = classnames(['select', { disabled, multiple }, className]);
        const selectClassNames = classnames(['select__select', { disabled, error: !!error }, inputClassName]);
        const handleChange = (value: any) => {
            const selected = multiple ? (value || []).map((v: any) => v.value) : value?.value;
            onChange(selected);
        };

        return (
            <label htmlFor={name} className={classNames} style={style}>
                {label && (
                    <Text tag="label" typo="label" for={id} className="select__label">
                        {label}
                    </Text>
                )}
                <div className="select__wrapper">
                    <ReactSelect
                        ref={ref}
                        id={id}
                        name={name}
                        placeholder={placeholder}
                        noOptionsMessage={() => noOptionsMessage}
                        options={options}
                        components={{
                            Option,
                            ...(!taggable && multiple
                                ? {
                                      ValueContainer: (props: any) => (
                                          <ValueContainer label={label} message={unTaggableMessage} {...props} />
                                      ),
                                  }
                                : {}),
                        }}
                        isMulti={multiple}
                        isDisabled={disabled}
                        hideSelectedOptions={hideSelectedOptions}
                        value={getValueForReactSelect(value, options, multiple, grouped)}
                        className={selectClassNames}
                        classNamePrefix="select"
                        onChange={handleChange}
                        {...otherProps}
                    />
                </div>
                {!!error && (
                    <Fade>
                        <Text tag="span" size="12" color="error" className="select__error">
                            {error}
                        </Text>
                    </Fade>
                )}
            </label>
        );
    }
);

export default Select;
